import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  MessageCircle,
  CheckCheck,
  StickyNote,
  ChevronDown,
  ArrowLeft,
  Edit3,
  Save,
  X,
  AlertCircle,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useSemmy } from '@/hooks/useSemmy';
import { SemmyAvatar } from '@/components/SemmyAvatar';
import { supabase, isSupabaseConfigured } from '@/lib/supabase';

/**
 * Semmy identity page — admin-only, reads and writes semmy_operator_context.
 *
 * Four editable fields from the operator context:
 *   - compiled_prompt     — Semmy's compiled knowledge of how the operator works
 *   - operator_identity   — Who the operator is, their trade, their context
 *   - hard_constraints    — What Semmy never does
 *   - learned_lessons     — Accumulated lessons from working together
 *
 * These fields shape EVERY WhatsApp reply Semmy sends and every internal
 * chat response. Editing here takes effect on the next message — no redeploy.
 *
 * Gated to admin emails via AdminOnly wrapper on the route.
 */

interface OperatorContext {
  compiled_prompt:    string;
  operator_identity:  string;
  hard_constraints:   string;
  learned_lessons:    string;
}

const EMPTY_CTX: OperatorContext = {
  compiled_prompt:   '',
  operator_identity: '',
  hard_constraints:  '',
  learned_lessons:   '',
};

const FIELDS: Array<{
  key:         keyof OperatorContext;
  label:       string;
  tagline:     string;
  placeholder: string;
  rows:        number;
}> = [
  {
    key:         'compiled_prompt',
    label:       'Aanpak',
    tagline:     'Hoe jij werkt — de kern van Semmy\'s kennis over jouw bedrijf',
    placeholder: 'Beschrijf hoe je werkt: je manier van klanten lezen, hoe je offertes aanpakt, wanneer je haast serieus neemt en wanneer niet, hoe je communiceert...',
    rows:        8,
  },
  {
    key:         'operator_identity',
    label:       'Identiteit',
    tagline:     'Wie je bent — achtergrond, vak, context',
    placeholder: 'Beschrijf jezelf en je bedrijf: je naam, je vak, je buurt, hoe lang je dit doet, wat je onderscheidt...',
    rows:        5,
  },
  {
    key:         'hard_constraints',
    label:       'Grenzen',
    tagline:     'Wat Semmy nooit doet, wat voor jou en je klanten niet werkt',
    placeholder: 'Wat Semmy nooit mag schrijven of doen. Bijv: geen prijzen noemen zonder meting, geen belofte over leverdata...',
    rows:        4,
  },
  {
    key:         'learned_lessons',
    label:       'Geleerde lessen',
    tagline:     'Wat jullie samenwerking heeft opgebracht — patronen en inzichten',
    placeholder: 'Wat Semmy heeft geleerd: welke toon werkt, welke vragen klanten stellen, wat altijd werkt...',
    rows:        5,
  },
];

export default function SemmyIdentityPage() {
  const navigate = useNavigate();
  const { effectivePartnerId } = useAuth();
  const { queued, completed, corrections, authority, loading: semmyLoading } = useSemmy(effectivePartnerId);

  const [ctx, setCtx]             = useState<OperatorContext>(EMPTY_CTX);
  const [ctxInit, setCtxInit]     = useState<OperatorContext>(EMPTY_CTX);
  const [dbLoading, setDbLoading] = useState(true);
  const [saving, setSaving]       = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [savedAt, setSavedAt]     = useState<string | null>(null);
  const [editing, setEditing]     = useState<keyof OperatorContext | null>(null);
  const [logExpanded, setLogExpanded] = useState<number | null>(0);

  useEffect(() => {
    if (!effectivePartnerId || !isSupabaseConfigured) { setDbLoading(false); return; }
    (async () => {
      const { data, error } = await supabase
        .from('semmy_operator_context')
        .select('compiled_prompt, operator_identity, hard_constraints, learned_lessons')
        .eq('partner_id', effectivePartnerId)
        .maybeSingle();
      setDbLoading(false);
      if (error || !data) return;
      const loaded: OperatorContext = {
        compiled_prompt:   (data as Record<string, unknown>).compiled_prompt   as string || '',
        operator_identity: (data as Record<string, unknown>).operator_identity as string || '',
        hard_constraints:  (data as Record<string, unknown>).hard_constraints  as string || '',
        learned_lessons:   (data as Record<string, unknown>).learned_lessons   as string || '',
      };
      setCtx(loaded);
      setCtxInit(loaded);
    })();
  }, [effectivePartnerId]);

  const anyDirty = JSON.stringify(ctx) !== JSON.stringify(ctxInit);
  const fieldDirty = (key: keyof OperatorContext) => ctx[key] !== ctxInit[key];

  async function saveAll() {
    if (!effectivePartnerId || !isSupabaseConfigured) return;
    setSaving(true);
    setSaveError(null);
    const { error } = await supabase
      .from('semmy_operator_context')
      .upsert(
        { partner_id: effectivePartnerId, ...ctx },
        { onConflict: 'partner_id' }
      );
    setSaving(false);
    if (error) {
      setSaveError(error.message);
    } else {
      setCtxInit({ ...ctx });
      setEditing(null);
      setSavedAt(new Date().toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' }));
      setTimeout(() => setSavedAt(null), 4000);
    }
  }

  function discardField(key: keyof OperatorContext) {
    setCtx(prev => ({ ...prev, [key]: ctxInit[key] }));
    setEditing(null);
  }

  const stats = useMemo(() => {
    const total    = queued.length + completed.length;
    const approved = completed.filter(a => a.status === 'approved' || a.status === 'sent').length;
    const approvalRate = total > 0 ? Math.round((approved / total) * 100) : 0;
    return { total, corrections: corrections.length, approvalRate };
  }, [queued, completed, corrections]);

  const tierLabel = useMemo(() => {
    if (!authority || authority.length === 0) return '—';
    const order: Record<string, number> = {
      suspended: 0, supervised: 1, proposal_queued: 2, autonomous: 3,
    };
    const top = [...authority].sort(
      (a, b) => (order[b.authority_status] ?? 0) - (order[a.authority_status] ?? 0),
    )[0];
    return top.authority_status.replace('_', ' ');
  }, [authority]);

  const LOG_ENTRIES = [
    { date: '20-5-2026', body: 'Mevrouw de Vries vroeg drie keer om een snellere installatie. Toon licht aangepast — kalmer in plaats van uitgebreid. Akkoord op woensdag, niet gehaast.', category: 'TONE' },
    { date: '18-5-2026', body: 'Telefoongesprek met Pieter K. opgepakt. Klant wilde direct prijs voor 240m². Niet meteen offerte; eerst gevraagd naar ondergrond.', category: 'TRIAGE' },
    { date: '15-5-2026', body: 'Vier opmerkingen verwerkt: kortere antwoorden bij eerste contact, prijs pas na bezoek, geen overdreven beloftes over levertijd.', category: 'NOTE' },
    { date: '12-5-2026', body: 'Eerste autonome bericht verstuurd zonder feedback — dank-je-wel na afspraakbevestiging. Klant reageerde positief. Drempel gehouden, niet verlaagd.', category: 'AUTONOMOUS' },
  ] as const;

  return (
    <div className="animate-fade-up max-w-7xl mx-auto pb-16">

      {/* Back */}
      <button
        onClick={() => navigate('/partner/semmy')}
        className="inline-flex items-center gap-1.5 text-[11px] mb-6 transition-opacity hover:opacity-100"
        style={{ color: 'var(--text-muted)', opacity: 0.6 }}
        aria-label="Terug naar wachtrij"
      >
        <ArrowLeft size={12} />
        <span>Wachtrij</span>
      </button>

      {/* Dirty banner */}
      {anyDirty && (
        <div
          className="sticky top-3 z-30 mb-6 flex items-center justify-between gap-3 px-4 py-3 rounded-xl border animate-fade-in"
          style={{
            background:  'var(--bg-surface-raised)',
            borderColor: 'var(--accent)',
            borderWidth: 1,
            boxShadow:   '0 4px 20px rgba(113,180,112,0.15)',
          }}
        >
          <div className="flex items-center gap-2">
            <AlertCircle size={14} style={{ color: 'var(--accent)' }} />
            <span className="text-[13px]" style={{ color: 'var(--text-secondary)' }}>
              Onopgeslagen wijzigingen — Semmy gebruikt de oude versie totdat je opslaat.
            </span>
          </div>
          <button
            onClick={saveAll}
            disabled={saving}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] font-semibold transition-all"
            style={{
              background: saving ? 'var(--bg-input)' : 'var(--accent)',
              color:      saving ? 'var(--text-muted)' : '#fff',
              border:     'none',
              cursor:     saving ? 'default' : 'pointer',
            }}
          >
            <Save size={12} />
            {saving ? 'Opslaan…' : 'Alles opslaan'}
          </button>
        </div>
      )}

      {/* Save confirmation */}
      {savedAt && !anyDirty && (
        <div
          className="mb-6 flex items-center gap-2 px-4 py-2 rounded-lg text-[12px] animate-fade-in"
          style={{
            background: 'rgba(113,180,112,0.08)',
            color: 'var(--accent)',
            border: '1px solid rgba(113,180,112,0.2)',
          }}
        >
          <CheckCheck size={13} />
          Opgeslagen om {savedAt} — Semmy gebruikt de nieuwe versie direct.
        </div>
      )}

      {saveError && (
        <div
          className="mb-6 flex items-center gap-2 px-4 py-2 rounded-lg text-[12px]"
          style={{ background: 'rgba(239,68,68,0.08)', color: '#f87171', border: '1px solid rgba(239,68,68,0.2)' }}
        >
          <AlertCircle size={13} />
          Fout bij opslaan: {saveError}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">

        {/* Left: editable fields */}
        <div className="space-y-6">
          <div>
            <span className="text-[11px] font-semibold uppercase tracking-wider"
              style={{ color: 'var(--accent)', letterSpacing: '0.12em' }}>
              AGENT IDENTITY
            </span>
            <h1 className="mt-3 font-bold tracking-tight"
              style={{ fontSize: 'clamp(48px, 7vw, 72px)', lineHeight: 1.02, letterSpacing: '-0.035em', color: 'var(--text-primary)' }}>
              semmy
            </h1>
            <p className="mt-2 text-[13px] italic" style={{ color: 'var(--text-tertiary)' }}>
              Dat het hout praat is geen poëzie — het is een vak.
            </p>
          </div>

          {dbLoading ? (
            <div className="space-y-3 mt-6">
              {[1, 2, 3, 4].map(i => (
                <div key={i} style={{ height: 80, background: 'var(--bg-surface-raised)', borderRadius: 12, opacity: 0.5 }} />
              ))}
            </div>
          ) : (
            <div className="space-y-3 mt-6">
              {FIELDS.map(field => {
                const isEditing = editing === field.key;
                const isDirty   = fieldDirty(field.key);
                const isEmpty   = !ctx[field.key]?.trim();
                return (
                  <div
                    key={field.key}
                    className="rounded-xl border transition-all"
                    style={{
                      background:  'var(--bg-surface-raised)',
                      borderColor: isEditing ? 'var(--accent)' : isDirty ? 'rgba(113,180,112,0.4)' : 'var(--border-separator)',
                      borderWidth: isEditing || isDirty ? 1.5 : 1,
                    }}
                  >
                    <div className="flex items-center justify-between px-4 pt-3 pb-2">
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="text-[10px] font-bold uppercase tracking-wider"
                            style={{ color: 'var(--accent)', letterSpacing: '0.1em' }}>
                            {field.label}
                          </span>
                          {isDirty && !isEditing && (
                            <span className="text-[9px] px-1.5 py-0.5 rounded-full font-semibold"
                              style={{ background: 'var(--accent-dim)', color: 'var(--accent)' }}>
                              gewijzigd
                            </span>
                          )}
                        </div>
                        <div className="text-[11px] mt-0.5" style={{ color: 'var(--text-muted)' }}>
                          {field.tagline}
                        </div>
                      </div>
                      {!isEditing && (
                        <button
                          onClick={() => setEditing(field.key)}
                          aria-label={`Bewerk ${field.label}`}
                          className="flex items-center gap-1 px-2 py-1 rounded-lg text-[11px] transition-all hover-surface"
                          style={{ color: 'var(--text-tertiary)' }}
                        >
                          <Edit3 size={11} />
                          <span>Bewerk</span>
                        </button>
                      )}
                    </div>
                    <div className="px-4 pb-3">
                      {isEditing ? (
                        <>
                          <textarea
                            value={ctx[field.key]}
                            onChange={e => setCtx(prev => ({ ...prev, [field.key]: e.target.value }))}
                            rows={field.rows}
                            placeholder={field.placeholder}
                            autoFocus
                            className="w-full text-[13px] leading-relaxed resize-y rounded-lg p-3 transition-all"
                            style={{
                              background: 'var(--bg-input)',
                              border:     '1px solid var(--border-active)',
                              color:      'var(--text-primary)',
                              outline:    'none',
                              fontFamily: 'inherit',
                              minHeight:  `${field.rows * 24}px`,
                            }}
                          />
                          <div className="flex justify-end gap-2 mt-2">
                            <button
                              onClick={() => discardField(field.key)}
                              className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[11px] transition-all hover-surface"
                              style={{ color: 'var(--text-muted)' }}
                              aria-label="Annuleer wijziging"
                            >
                              <X size={11} />
                              <span>Annuleer</span>
                            </button>
                            <button
                              onClick={() => setEditing(null)}
                              className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[11px] font-medium transition-all"
                              style={{ background: 'var(--accent-dim)', color: 'var(--accent)', border: 'none' }}
                            >
                              <Save size={11} />
                              <span>Klaar</span>
                            </button>
                          </div>
                        </>
                      ) : (
                        <div
                          className="text-[12px] leading-relaxed whitespace-pre-wrap cursor-text"
                          style={{
                            color:     isEmpty ? 'var(--text-muted)' : 'var(--text-secondary)',
                            fontStyle: isEmpty ? 'italic' : 'normal',
                            minHeight: 32,
                          }}
                          onClick={() => setEditing(field.key)}
                          role="button"
                          tabIndex={0}
                          onKeyDown={e => e.key === 'Enter' && setEditing(field.key)}
                          aria-label={`Bewerk ${field.label}`}
                        >
                          {isEmpty ? field.placeholder.slice(0, 80) + '…' : ctx[field.key]}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Right: avatar + stats + log */}
        <div className="space-y-6">

          {/* Avatar */}
          <div className="flex flex-col items-center text-center pt-2">
            <div className="relative">
              <div
                className="absolute rounded-full pointer-events-none"
                style={{
                  inset:      -12,
                  background: 'conic-gradient(from 0deg, rgba(113,180,112,0.0) 0deg, rgba(113,180,112,0.18) 120deg, rgba(200,240,210,0.32) 180deg, rgba(113,180,112,0.18) 240deg, rgba(113,180,112,0.0) 360deg)',
                  filter:     'blur(14px)',
                  opacity:    0.7,
                }}
              />
              <div
                className="relative rounded-full flex items-center justify-center"
                style={{
                  width:      132,
                  height:     132,
                  background: 'var(--bg-surface-raised)',
                  border:     '1px solid var(--accent)',
                  boxShadow:  '0 0 0 4px var(--bg-surface), 0 0 32px rgba(113,180,112,0.20)',
                }}
              >
                <SemmyAvatar size={96} />
              </div>
            </div>
            <h2 className="mt-5 font-bold tracking-tight"
              style={{ fontSize: 56, lineHeight: 1, letterSpacing: '-0.03em', color: 'var(--text-primary)' }}>
              semmy
            </h2>
            <p className="mt-2 text-[13px]" style={{ color: 'var(--text-tertiary)' }}>
              De stem van het vak — Jordaan, sinds nu
            </p>
            <p className="mt-1 text-[11px]" style={{ color: 'var(--text-muted)' }}>
              {tierLabel}
            </p>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-3">
            <StatTile icon={<CheckCheck size={14} />} label="GOEDGEKEURD" value={semmyLoading ? '—' : `${stats.approvalRate}`} suffix="%" />
            <StatTile icon={<MessageCircle size={14} />} label="GESPREKKEN" value={semmyLoading ? '—' : `${stats.total}`} />
            <StatTile icon={<StickyNote size={14} />} label="CORRECTIES" value={semmyLoading ? '—' : `${stats.corrections}`} />
          </div>

          {/* Active compiled_prompt preview */}
          {ctxInit.compiled_prompt && (
            <div className="rounded-xl p-4 border"
              style={{ background: 'var(--bg-surface-raised)', borderColor: 'var(--accent)', borderLeftWidth: 2 }}>
              <div className="flex items-center gap-2 mb-2">
                <span className="text-[10px] font-semibold uppercase tracking-wider"
                  style={{ color: 'var(--accent)', letterSpacing: '0.12em' }}>
                  ACTIEVE VERSIE — WAT SEMMY NU GEBRUIKT
                </span>
              </div>
              <p className="text-[12px] leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
                {ctxInit.compiled_prompt.slice(0, 280)}
                {ctxInit.compiled_prompt.length > 280 && <span style={{ color: 'var(--text-muted)' }}>…</span>}
              </p>
              {anyDirty && (
                <p className="mt-2 text-[11px] italic" style={{ color: 'var(--text-muted)' }}>
                  Sla op om Semmy bij te werken.
                </p>
              )}
            </div>
          )}

          {/* Featured log entry */}
          <div className="rounded-xl p-5 border"
            style={{ background: 'var(--bg-surface-raised)', borderColor: 'var(--accent)', borderLeftWidth: 2 }}>
            <div className="flex items-center justify-between mb-3">
              <span className="text-[10px] font-semibold uppercase tracking-wider"
                style={{ color: 'var(--accent)', letterSpacing: '0.12em' }}>
                SEMMY LOG
              </span>
              <span className="text-[10px] tabular-nums" style={{ color: 'var(--text-muted)' }}>
                {LOG_ENTRIES[0]?.date}
              </span>
            </div>
            <p className="text-[13px] leading-relaxed italic" style={{ color: 'var(--text-secondary)' }}>
              {LOG_ENTRIES[0]?.body}
            </p>
          </div>

          {/* Older notes */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <StickyNote size={12} style={{ color: 'var(--text-muted)' }} />
                <span className="text-[11px] uppercase tracking-wider font-semibold"
                  style={{ color: 'var(--text-muted)', letterSpacing: '0.1em' }}>
                  Notities
                </span>
              </div>
              <span className="text-[10px]" style={{ color: 'var(--text-muted)' }}>
                {LOG_ENTRIES.length} totaal
              </span>
            </div>
            <div className="space-y-2">
              {LOG_ENTRIES.slice(1).map((entry, idx) => {
                const expanded = logExpanded === idx + 1;
                return (
                  <button
                    key={idx}
                    onClick={() => setLogExpanded(expanded ? null : idx + 1)}
                    className="w-full text-left rounded-xl p-3 border transition-all hover-surface"
                    style={{ background: 'var(--bg-surface-raised)', borderColor: 'var(--border-separator)' }}
                    aria-expanded={expanded}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-[9px] font-bold tracking-wider px-1.5 py-0.5 rounded"
                        style={{ background: 'var(--accent-dim)', color: 'var(--accent)', letterSpacing: '0.1em' }}>
                        {entry.category}
                      </span>
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] tabular-nums" style={{ color: 'var(--text-muted)' }}>
                          {entry.date}
                        </span>
                        <ChevronDown size={12} style={{
                          color: 'var(--text-muted)',
                          transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                          transition: 'transform 0.2s ease',
                        }} />
                      </div>
                    </div>
                    <div style={{ maxHeight: expanded ? 200 : 32, overflow: 'hidden', transition: 'max-height 0.25s ease' }}>
                      <p className="text-[12px] leading-relaxed"
                        style={{ color: 'var(--text-secondary)', marginTop: expanded ? 6 : 0 }}>
                        {entry.body}
                      </p>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="text-center pt-2">
            <p className="text-[10px]" style={{ color: 'var(--text-muted)' }}>
              Alleen voor admin · Wijzigingen zijn direct actief na opslaan
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function StatTile({ icon, label, value, suffix }: {
  icon: React.ReactNode; label: string; value: string; suffix?: string;
}) {
  return (
    <div className="rounded-xl p-3 border flex flex-col items-center text-center"
      style={{ background: 'var(--bg-surface-raised)', borderColor: 'var(--border-separator)' }}>
      <div className="w-7 h-7 rounded-full flex items-center justify-center mb-2"
        style={{ background: 'var(--accent-dim)', color: 'var(--accent)' }}>
        {icon}
      </div>
      <div className="text-[9px] font-semibold uppercase tracking-wider mb-1"
        style={{ color: 'var(--text-muted)', letterSpacing: '0.1em' }}>
        {label}
      </div>
      <div className="flex items-baseline gap-0.5">
        <span className="font-bold tabular-nums"
          style={{ fontSize: 28, color: 'var(--text-primary)', letterSpacing: '-0.02em' }}>
          {value}
        </span>
        {suffix && <span className="text-[12px]" style={{ color: 'var(--text-muted)' }}>{suffix}</span>}
      </div>
    </div>
  );
}
